import { Element, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
  BlockPaddingWrapper,
} from "@/components/editor/shared/block-wrapper";
import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";

type AboutSummaryProps = {
  title: string;
  paragraphHeading: string;
  paragraph: string;
};

export default function AboutSummary(props: AboutSummaryProps) {
  const {
    connectors: { connect },
    nodeId,
  } = useNode((node) => ({
    nodeId: node.id,
  }));

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "paragraphHeading",
    "paragraph",
  ]);

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <BlockPaddingWrapper>
        <BlockMaxWidthWrapper>
          <Content>
            <div className="flex justify-center">
              <Element
                id="title"
                is={Text}
                text={props.title}
                element="h2"
                pattern="headingTwo"
              />
            </div>
            <div className="body-content">
              {isRichTextCompatible ? (
                <>
                  <Element
                    id="paragraphHeading"
                    is={RichText}
                    text={`<h4>${props.paragraphHeading}</h4>`}
                  />
                  <Element
                    id="paragraph"
                    is={RichText}
                    text={`<p>${props.paragraph}</p>`}
                  />
                </>
              ) : (
                <>
                  <Element
                    id="paragraphHeading"
                    is={Text}
                    text={props.paragraphHeading}
                    element="h3"
                    pattern="headingThree"
                  />
                  <Element
                    id="paragraph"
                    is={Text}
                    text={props.paragraph}
                    pattern="paragraph"
                  />
                </>
              )}
            </div>
          </Content>
        </BlockMaxWidthWrapper>
      </BlockPaddingWrapper>
    </BlockBackgroundColorWrapper>
  );
}

AboutSummary.craft = {
  custom: {
    displayName: "About Summary",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
  },
  rules: {
    canMoveIn: () => false,
  },
};

const Content = withViewportStyle("div")`
  .headingTwo,
  .headingThree {
    margin: 0;
  }
  
  .body-content {
    display: grid;
    grid-template-columns: .55fr 1fr;
    gap: ${rem(24)};
    margin-top: ${rem(40)};
    
    ${(props) => props.$viewport.mobile`
      grid-template-columns: 1fr;
      gap: ${rem(8)};
    `}
  }
`;
